import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import MsBHUMultiSiteModal from './component'

// Helper functions
const getPreferenceValue = site => site?.preferences?.value ?? false

const findOriginalSite = (siteId, rawSitesData) =>
  rawSitesData.find(site => site.siteId === siteId)

const countSitesWithBHUnit = sites =>
  sites.filter(site => getPreferenceValue(site)).length

const countSitesToUpdate = (listedSites, rawSitesData) =>
  listedSites.filter(site => {
    const originalSite = findOriginalSite(site.siteId, rawSitesData)
    return getPreferenceValue(site) !== getPreferenceValue(originalSite)
  }).length

const MsBHUMultiSiteModalContainer = ({
  onModalSave,
  onModalCancel,
  rawSitesData,
}) => {
  const [listedSites, setListedSites] = useState([])
  const [countOfSitesToUpdate, setCountOfSitesToUpdate] = useState(0)
  const [selectedSiteIds, setSelectedSiteIds] = useState([])
  const [sitesHavingBHUnitCount, setSitesHavingBHUnitCount] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isSaving) return

    setSitesHavingBHUnitCount(countSitesWithBHUnit(rawSitesData))
    setListedSites(rawSitesData)
  }, [isSaving, rawSitesData])

  const handleSave = async () => {
    const sitesToUpdate = listedSites.reduce((bullyingSites, currentSite) => {
      const currentBullyingValue = getPreferenceValue(currentSite)
      const originalSite = findOriginalSite(currentSite.siteId, rawSitesData)
      const originalBullyingValue = getPreferenceValue(originalSite)

      if (currentBullyingValue !== originalBullyingValue) {
        const basePreference =
          originalSite?.preferences || currentSite?.preferences
        const preference = {
          ...basePreference,
          value: currentBullyingValue,
        }
        bullyingSites.push({
          siteId: currentSite.siteId,
          preferences: [preference],
        })
      }
      return bullyingSites
    }, [])
    setIsSaving(true)
    try {
      await onModalSave(sitesToUpdate)
    } catch (error) {
      setIsSaving(false)
      throw error
    }
  }

  const selectAllSites = e => {
    e.stopPropagation()
    if (listedSites.length === selectedSiteIds.length) {
      setSelectedSiteIds([])
    } else {
      setSelectedSiteIds(rawSitesData.map(site => site.siteId))
    }
  }

  const onCheckBoxClick = (event, eventSiteId) => {
    event.stopPropagation()
    if (event.target.checked) {
      setSelectedSiteIds([...selectedSiteIds, eventSiteId])
    } else {
      setSelectedSiteIds(
        selectedSiteIds.filter(siteId => siteId !== eventSiteId),
      )
    }
  }

  const changeBHUStatus = value => {
    const updatedSites = listedSites.map(site => {
      const isSelected = selectedSiteIds.includes(site.siteId)
      const updatedValue = isSelected ? value : getPreferenceValue(site)
      return {
        ...site,
        preferences: {
          ...site.preferences,
          value: updatedValue,
        },
      }
    })

    setListedSites(updatedSites)
    setSitesHavingBHUnitCount(countSitesWithBHUnit(updatedSites))
    setCountOfSitesToUpdate(countSitesToUpdate(updatedSites, rawSitesData))
  }

  const allSelected =
    listedSites.length > 0 && listedSites.length === selectedSiteIds.length
  const indeterminate =
    listedSites.length !== selectedSiteIds.length && selectedSiteIds.length > 0
  return (
    <MsBHUMultiSiteModal
      allSelected={allSelected}
      changeBHUStatus={changeBHUStatus}
      indeterminate={indeterminate}
      isSaveButtonEnabled={countOfSitesToUpdate > 0 && !isSaving}
      onCheckBoxClick={onCheckBoxClick}
      onModalCancel={onModalCancel}
      onModalSave={handleSave}
      selectAllSites={selectAllSites}
      selectedSiteIds={selectedSiteIds}
      setListedSites={setListedSites}
      sites={listedSites}
      sitesHavingBHUnitCount={sitesHavingBHUnitCount}
    />
  )
}

MsBHUMultiSiteModalContainer.propTypes = {
  onModalCancel: PropTypes.func,
  onModalSave: PropTypes.func.isRequired,
  rawSitesData: PropTypes.arrayOf(
    PropTypes.shape({
      siteId: PropTypes.number,
      name: PropTypes.string,
      preferences: PropTypes.shape({
        value: PropTypes.bool,
        name: PropTypes.string,
      }),
    }),
  ).isRequired,
}

export default MsBHUMultiSiteModalContainer
